/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, FormControlLabel, IconButton, Menu, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { automationsAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { SKIP_GUARD, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import ActionEditor from '../../components/automation/ActionEditor'
import ValueEditor from '../../components/automation/ValueEditor'
import { useDescribeRule } from '../../components/automation/RuleSummary'
import { useEntityFields } from '../../components/automation/useEntityFields'
import {
  ACTIONS, EVERY, OP_LABELS, SYSTEM_DATES, TRIGGERS, UNARY, WEEKDAYS, cleanTrigger, emptyRule, isTimeBased, newAction, opsFor, todayOffset, todayToken,
} from '../../components/automation/automationModel'
import { useFeatures } from '../../hooks/useCrmQueries'
import { InputAdornment } from '@mui/material'
import { entityLabel } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'

const Section = ({ step, title, hint, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
    <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>{step}</Typography>
    <Typography variant="h6" sx={{ mb: hint ? 0.25 : 2 }}>{title}</Typography>
    {hint && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>}
    {children}
  </Paper>
)

const AutomationEditPage = () => {
  const { ruleId } = useParams()
  const isNew = !ruleId
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const notify = useNotify()
  const queryClient = useQueryClient()
  const { entityTypes } = useEntityTypes()

  const { data: saved, isLoading } = useQuery({ queryKey: [tenantId, 'automations', ruleId], queryFn: () => automationsAPI.get(ruleId), enabled: !isNew })
  const [rule, setRule] = useState(null)
  const [initial, setInitial] = useState(null)
  useEffect(() => {
    const start = isNew ? emptyRule(entityTypes[0] || '') : saved && { name: saved.name, entityType: saved.entityType, enabled: saved.enabled, definition: saved.definition }
    if (start && !rule) { setRule(start); setInitial(start) }
    // opened directly: the record types may arrive after the page did
    if (isNew && rule && !rule.entityType && entityTypes[0]) {
      const withType = { ...rule, entityType: entityTypes[0] }
      setRule(withType)
      setInitial(withType)
    }
  }, [isNew, saved, entityTypes, rule])

  const { fields, byKey } = useEntityFields(rule?.entityType)
  const timeBased = isTimeBased(rule?.definition?.trigger)
  const ctx = useMemo(() => ({ entityType: rule?.entityType, sourceFields: fields, sourceByKey: byKey, timeBased }), [rule?.entityType, fields, byKey, timeBased])
  const { emailEnabled } = useFeatures()
  const describe = useDescribeRule(rule?.entityType)
  const [stepAnchor, setStepAnchor] = useState(null)
  const dirty = !!rule && JSON.stringify(rule) !== JSON.stringify(initial)
  const guard = useUnsavedChangesGuard(dirty)

  const save = useMutation({
    mutationFn: (body) => (isNew ? automationsAPI.create(body) : automationsAPI.update(ruleId, body)),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'automations'] })
      notify(isNew ? `“${result.name}” created${result.enabled ? ' and on' : ''}` : 'Automation saved')
      navigate('/settings/automations', SKIP_GUARD)
    },
  })

  if (!rule || (!isNew && isLoading)) return <Loading />

  const def = rule.definition
  const setDef = (patch) => setRule((r) => ({ ...r, definition: { ...r.definition, ...patch } }))
  const setTrigger = (patch) => setDef({ trigger: cleanTrigger({ ...def.trigger, ...patch }) })
  const setCondition = (i, patch) => setDef({ conditions: def.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) })
  const setAction = (i, action) => setDef({ actions: def.actions.map((a, j) => (j === i ? action : a)) })
  const moveAction = (i, delta) => {
    const actions = [...def.actions]
    const [moved] = actions.splice(i, 1)
    actions.splice(i + delta, 0, moved)
    setDef({ actions })
  }
  const onDelete = def.trigger.type === 'DELETED'
  const dateFields = fields.filter((f) => f.fieldType === 'DATE' || f.fieldType === 'DATETIME')
  const trigger = def.trigger
  const offset = trigger.offsetDays || 0
  const noun = entityLabel(rule.entityType || 'RECORD').toLowerCase()
  const errors = save.error ? getErrorList(save.error) : []

  return (
    <Box sx={{ maxWidth: 920 }}>
      {guard}
      <PageHeader title={isNew ? 'New automation' : rule.name || 'Automation'}
        subtitle={isNew ? 'Choose when it runs, what has to be true, and what it does' : `Last saved by ${saved?.updatedBy || '—'}`}
        actions={(
          <>
            <Button onClick={() => navigate('/settings/automations')}>Cancel</Button>
            <Button variant="contained" disabled={save.isPending || !rule.name.trim() || !rule.entityType || !def.actions.length}
              onClick={() => save.mutate({ ...rule, name: rule.name.trim() })}>
              {save.isPending ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          </>
        )} />

      {save.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(save.error)}
          {errors.length > 0 && <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{errors.map((e) => <li key={e}>{e}</li>)}</Box>}
        </Alert>
      )}

      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField label="Name" required value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} sx={{ flex: 1 }}
              placeholder="e.g. Big deal won" inputProps={{ maxLength: 120 }} autoFocus={isNew} />
            <TextField select label="Record type" value={rule.entityType} sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
              onChange={(e) => setRule({ ...rule, entityType: e.target.value, definition: emptyRule().definition })}
              helperText={isNew ? undefined : 'Changing it clears the steps'}>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabel(t)}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Switch checked={rule.enabled} onChange={(e) => setRule({ ...rule, enabled: e.target.checked })} />} label="On" />
          </Stack>
        </Paper>

        <Section step="When" title="Trigger">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <Typography sx={{ pt: { sm: 1 }, whiteSpace: 'nowrap' }}>{trigger.type === 'SCHEDULE' ? 'Run' : `When a ${noun}`}</Typography>
            <TextField select value={def.trigger.type} onChange={(e) => setDef({
                trigger: cleanTrigger({ type: e.target.value }),
                // "Last updated" / "Created" only exist for time-based rules
                ...(isTimeBased({ type: e.target.value }) ? {} : { conditions: def.conditions.filter((c) => !c.field?.startsWith('$')) }),
              })} sx={{ width: { xs: '100%', sm: 240 }, flexShrink: 0 }} aria-label="Trigger">
              {TRIGGERS.map((t) => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
            </TextField>
            {def.trigger.type === 'FIELD_CHANGED' && (
              <TextField select label="Field" value={def.trigger.field || ''} sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }} onChange={(e) => setTrigger({ field: e.target.value, to: null })}>
                {fields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
            )}
          </Stack>
          {trigger.type === 'DATE_REACHED' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }} alignItems={{ sm: 'flex-start' }}>
              {offset !== 0 && (
                <TextField label="Days" type="number" value={Math.abs(offset)} sx={{ width: { xs: '100%', sm: 110 } }} inputProps={{ min: 1, max: 365 }}
                  onChange={(e) => {
                    const n = Math.min(365, Math.max(1, Math.trunc(Number(e.target.value) || 1)))
                    setTrigger({ offsetDays: offset < 0 ? -n : n })
                  }} />
              )}
              <TextField select label="When" value={offset < 0 ? 'before' : offset > 0 ? 'after' : 'on'} sx={{ width: { xs: '100%', sm: 130 } }}
                onChange={(e) => setTrigger({ offsetDays: e.target.value === 'on' ? 0 : (e.target.value === 'before' ? -1 : 1) * Math.max(1, Math.abs(offset)) })}>
                <MenuItem value="before">before</MenuItem>
                <MenuItem value="on">on (same day)</MenuItem>
                <MenuItem value="after">after</MenuItem>
              </TextField>
              <TextField select label="Date field" value={trigger.field || ''} sx={{ width: { xs: '100%', sm: 220 } }} onChange={(e) => setTrigger({ field: e.target.value })}
                helperText={dateFields.length ? undefined : 'This record type has no date fields'}>
                {dateFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              {byKey[trigger.field]?.fieldType !== 'DATETIME' && (
                <TextField label="At" type="time" value={trigger.at || '09:00'} sx={{ width: { xs: '100%', sm: 130 } }} InputLabelProps={{ shrink: true }}
                  onChange={(e) => setTrigger({ at: e.target.value })} />
              )}
            </Stack>
          )}
          {trigger.type === 'SCHEDULE' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }} alignItems={{ sm: 'flex-start' }}>
              <TextField select label="Repeat" value={trigger.every || 'DAILY'} sx={{ width: { xs: '100%', sm: 170 } }} onChange={(e) => setTrigger({ every: e.target.value })}>
                {EVERY.map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
              </TextField>
              {trigger.every === 'WEEKLY' && (
                <TextField select label="On" value={trigger.dayOfWeek || 1} sx={{ width: { xs: '100%', sm: 160 } }} onChange={(e) => setTrigger({ dayOfWeek: Number(e.target.value) })}>
                  {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i + 1}>{d}</MenuItem>)}
                </TextField>
              )}
              {trigger.every === 'MONTHLY' && (
                <TextField select label="On day" value={trigger.dayOfMonth || 1} sx={{ width: { xs: '100%', sm: 120 } }} onChange={(e) => setTrigger({ dayOfMonth: Number(e.target.value) })}>
                  {Array.from({ length: 28 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>)}
                </TextField>
              )}
              <TextField label="At" type="time" value={trigger.at || '09:00'} sx={{ width: { xs: '100%', sm: 130 } }} InputLabelProps={{ shrink: true }}
                onChange={(e) => setTrigger({ at: e.target.value })} />
            </Stack>
          )}
          {timeBased && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {trigger.type === 'SCHEDULE'
                ? `Runs at that time for every ${noun} (up to 1,000); add conditions to pick which ones. Use “Last updated” to find ones nobody touched.`
                : `Runs once per ${noun} and date, at most a minute after the moment. Changing the date makes it run again.`}
              {' '}Nobody “made a change” here, so choose people from the record or by name.
            </Alert>
          )}
          {def.trigger.type === 'FIELD_CHANGED' && byKey[def.trigger.field] && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }} alignItems={{ sm: 'flex-start' }}>
              <TextField select label="Changes to" value={def.trigger.to == null ? 'any' : 'value'} sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                onChange={(e) => setTrigger({ to: e.target.value === 'any' ? null : '' })}>
                <MenuItem value="any">Any value</MenuItem>
                <MenuItem value="value">A specific value</MenuItem>
              </TextField>
              {def.trigger.to != null && (
                <ValueEditor field={byKey[def.trigger.field]} value={def.trigger.to || null} onChange={(to) => setTrigger({ to: to ?? '' })} ctx={ctx} label={byKey[def.trigger.field].label} />
              )}
            </Stack>
          )}
        </Section>

        <Section step="Only if" title="Conditions" hint={timeBased
          ? 'Optional. Every condition must hold for the steps to run, checked against the record as it is at that time.'
          : 'Optional. Every condition must hold for the steps to run; values are checked after the change.'}>
          <Stack spacing={1.5}>
            {def.conditions.map((c, i) => {
              const system = SYSTEM_DATES.find((f) => f.fieldKey === c.field)
              if (system) {
                return (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                    <TextField select label="Field" value={c.field} sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
                      onChange={(e) => setCondition(i, { field: e.target.value })}>
                      {SYSTEM_DATES.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                      {fields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                    </TextField>
                    <TextField select label="Is" value={c.op === 'lt' ? 'lt' : 'gte'} sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                      onChange={(e) => setCondition(i, { op: e.target.value })}>
                      <MenuItem value="lt">more than … days ago</MenuItem>
                      <MenuItem value="gte">within the last … days</MenuItem>
                    </TextField>
                    <TextField label="Days" type="number" value={Math.abs(todayOffset(c.value))} sx={{ width: { xs: '100%', sm: 130 } }}
                      InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                      onChange={(e) => setCondition(i, { value: todayToken(-Math.min(3650, Math.max(0, Math.trunc(Number(e.target.value) || 0)))) })} />
                    <Box sx={{ flex: 1 }} />
                    <IconButton onClick={() => setDef({ conditions: def.conditions.filter((_, j) => j !== i) })} aria-label="Remove condition" sx={{ mt: 0.5 }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )
              }
              const field = byKey[c.field]
              return (
                <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField select label="Field" value={c.field || ''} sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
                    onChange={(e) => setCondition(i, e.target.value.startsWith('$')
                      ? { field: e.target.value, op: 'lt', value: '$today-7' }
                      : { field: e.target.value, op: opsFor(byKey[e.target.value])[0], value: null })}>
                    {timeBased && SYSTEM_DATES.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                    {fields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
                  </TextField>
                  <TextField select label="Operator" value={c.op || ''} sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }} disabled={!field}
                    onChange={(e) => setCondition(i, { op: e.target.value, ...(UNARY.includes(e.target.value) ? { value: null } : {}) })}>
                    {(field ? opsFor(field) : []).map((op) => <MenuItem key={op} value={op}>{OP_LABELS[op]}</MenuItem>)}
                  </TextField>
                  {field && !UNARY.includes(c.op) && (
                    <ValueEditor field={field} value={c.value} onChange={(value) => setCondition(i, { value })} ctx={ctx} label={field.label} />
                  )}
                  {(!field || UNARY.includes(c.op)) && <Box sx={{ flex: 1 }} />}
                  <IconButton onClick={() => setDef({ conditions: def.conditions.filter((_, j) => j !== i) })} aria-label="Remove condition" sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )
            })}
            <Box>
              <Button size="small" startIcon={<AddIcon />} disabled={!fields.length || def.conditions.length >= 10}
                onClick={() => setDef({ conditions: [...def.conditions, { field: '', op: '', value: null }] })}>
                Add condition
              </Button>
            </Box>
          </Stack>
        </Section>

        <Section step="Then" title="Steps" hint="Run in order. If a step fails, the steps after it are skipped; steps already done stay done.">
          <Stack spacing={1.5}>
            {def.actions.map((action, i) => (
              <ActionEditor key={i} action={action} index={i} count={def.actions.length} ctx={ctx}
                onChange={(a) => setAction(i, a)} onMove={(d) => moveAction(i, d)}
                onRemove={() => setDef({ actions: def.actions.filter((_, j) => j !== i) })} />
            ))}
            <Box>
              <Button variant="outlined" startIcon={<AddIcon />} disabled={def.actions.length >= 10 || !rule.entityType} onClick={(e) => setStepAnchor(e.currentTarget)}>
                Add step
              </Button>
              <Menu anchorEl={stepAnchor} open={!!stepAnchor} onClose={() => setStepAnchor(null)}>
                {ACTIONS.filter((a) => !a.needsEmail || emailEnabled).map((a) => (
                  <MenuItem key={a.type} disabled={onDelete && ['UPDATE_RECORD', 'SEND_EMAIL', 'ASSIGN_ROUND_ROBIN'].includes(a.type)}
                    onClick={() => { setStepAnchor(null); setDef({ actions: [...def.actions, newAction(a.type)] }) }}>
                    <Box>
                      <Typography variant="body2">{a.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.hint}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Stack>
        </Section>

        {rule.entityType && (
          <Alert severity="info" icon={false}>
            <Typography variant="subtitle2">In plain words</Typography>
            <Typography variant="body2">{describe(def)}</Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  )
}

export default AutomationEditPage
